import { DocumentDuplicateIcon } from '@heroicons/react/outline';
import { CheckIcon } from '@heroicons/react/solid';
import topUpBankAccountIcon from 'assets/svg/icons/top_up_bank_account_icon.svg';
import { Button } from 'components/atoms/Button';
import useTransfer from 'hooks/useTransfer';
import { motion } from 'motion/react';
import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { Connector } from 'types/transactions.types';
import { WEBAPP_BANKING_NUDGES_CLICKEDSETASIDE } from 'utils/amplitude/events/bank.amplitude';
import { copy } from 'utils/helpers';
import { friendlyFormatIBAN } from 'ibantools';

const transition = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { delay: 0.25 } },
  exit: { opacity: 0 },
};

export const WalletTopUpBankAccount: FC<{
  connector?: Connector;
  showCTA?: boolean;
}> = ({ connector, showCTA = true }) => {
  const { t } = useTranslation();

  const [copiedName, setCopiedName] = useState(false);

  const [copiedIBAN, setCopiedIBAN] = useState(false);

  const [copiedBIC, setCopiedBIC] = useState(false);

  const transfer = useTransfer();

  return (
    <div className="flex items-center flex-col relative h-full px-6 w-full">
      <motion.div
        {...transition}
        className="w-10 h-10 p-1.5 bg-bank-100 rounded-full text-bank-900 stroke-bank-900 fill-bank-900"
      >
        <img src={topUpBankAccountIcon} />
      </motion.div>

      <motion.div
        {...{
          initial: { opacity: 0 },
          animate: { opacity: 1, transition: { delay: 0.25 } },
          exit: { opacity: 0 },
        }}
        className="my-4 text-center"
      >
        <h1 className="text-2xl font-bold text-primary-800 w-[90%] mx-auto">
          {t('accountable_banking.add_money.label')}
        </h1>

        <p className="text-primary-700 mt-2">
          {t('accountable_banking.top_up.subtitle')}
        </p>
      </motion.div>

      <motion.div
        {...transition}
        className="my-4 text-center border border-taxes-100 rounded-lg w-full"
      >
        <div className="flex items-center justify-between p-5 border-b border-taxes-100">
          <div className="flex flex-col items-start">
            <h3 className="text-primary-700">
              {t('customer.field.account_holder_name')}
            </h3>

            <p className="text-primary font-semibold text-lg">
              {connector?.bankAccountReference.accountHolderName}
            </p>
          </div>

          <button
            className="ml-1"
            onClick={() =>
              copy(
                connector?.bankAccountReference?.accountHolderName as string,
              ).then((success) => {
                if (success) {
                  setCopiedName(true);
                }
              })
            }
          >
            {copiedName ? (
              <CheckIcon className="w-[20px] text-green-500" />
            ) : (
              <DocumentDuplicateIcon className="w-[20px] text-primary" />
            )}
          </button>
        </div>

        <div className="flex items-center justify-between p-5 border-b border-taxes-100">
          <div className="flex flex-col items-start">
            <h3 className="text-primary-700">IBAN</h3>

            <p className="text-primary font-semibold text-lg">
              {friendlyFormatIBAN(connector?.bankAccountReference?.IBAN)}
            </p>
          </div>

          <button
            className="ml-1"
            onClick={() =>
              copy(connector?.bankAccountReference?.IBAN as string).then(
                (success) => {
                  if (success) {
                    setCopiedIBAN(true);
                  }
                },
              )
            }
          >
            {copiedIBAN ? (
              <CheckIcon className="w-[20px] text-green-500" />
            ) : (
              <DocumentDuplicateIcon className="w-[20px] text-primary" />
            )}
          </button>
        </div>

        <div className="flex items-center justify-between p-5">
          <div className="flex flex-col items-start">
            <h3 className="text-primary-700">BIC</h3>

            <p className="text-primary font-semibold text-lg">
              {connector?.bankAccountReference?.BIC}
            </p>
          </div>

          <button
            className="ml-1"
            onClick={() =>
              copy(connector?.bankAccountReference?.BIC as string).then(
                (success) => {
                  if (success) {
                    setCopiedBIC(true);
                  }
                },
              )
            }
          >
            {copiedBIC ? (
              <CheckIcon className="w-[20px] text-green-500" />
            ) : (
              <DocumentDuplicateIcon className="w-[20px] text-primary" />
            )}
          </button>
        </div>
      </motion.div>

      <motion.div {...transition}>
        <p className="text-primary-700 mt-2 text-sm">
          {t('accountable_banking.top_up.transfer_time_disclaimer')}
        </p>

        {showCTA ? (
          <Button
            className="mt-10 w-full"
            color="bank"
            onClick={() =>
              transfer({
                receiptAccount: connector,
              })
            }
            tracingEvent={WEBAPP_BANKING_NUDGES_CLICKEDSETASIDE}
          >
            {t('accountable_banking.set_aside.cta')}
          </Button>
        ) : null}
      </motion.div>
    </div>
  );
};
